import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Mail, RefreshCw, Search, Filter, Eye, Send, 
  CheckCircle, XCircle, Clock, MailOpen, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = window.location.origin;

const HistorialNotificaciones = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    estado: '',
    tipo: '',
    leido: '',
    buscar: ''
  });
  const [tiposDisponibles, setTiposDisponibles] = useState([]);
  
  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.leido) params.append('leido', filters.leido === 'true');
      if (filters.buscar) params.append('buscar', filters.buscar);
      
      const response = await axios.get(`${API}/api/admin/notifications/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setTiposDisponibles(response.data.tipos_disponibles || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/notifications/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [loadLogs]);

  const handleResend = async (notificationId) => {
    try {
      setResending(notificationId);
      await axios.post(`${API}/api/admin/notifications/${notificationId}/resend`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      toast.success('Email reenviado exitosamente');
      loadLogs();
      loadStats();
    } catch (error) {
      console.error('Error resending:', error);
      toast.error(error.response?.data?.detail || 'Error al reenviar');
    } finally {
      setResending(null);
    }
  };

  const handleResendAllFailed = async () => {
    if (!window.confirm('¿Reenviar todas las notificaciones fallidas?')) return;
    
    try {
      setResendingAll(true);
      const response = await axios.post(`${API}/api/admin/notifications/resend-failed`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      toast.success(response.data.message);
      loadLogs();
      loadStats();
    } catch (error) {
      console.error('Error resending all:', error);
      toast.error(error.response?.data?.detail || 'Error al reenviar');
    } finally {
      setResendingAll(false);
    }
  };

  const handlePreview = async (notificationId) => {
    try {
      setLoadingPreview(true);
      setPreviewOpen(true);
      const response = await axios.get(`${API}/api/admin/notifications/${notificationId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setPreviewData(response.data);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Error al cargar vista previa');
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ estado: '', tipo: '', leido: '', buscar: '' });
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-UY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (log) => {
    if (log.estado === 'fallido') {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Fallido
        </Badge>
      );
    }
    if (log.estado === 'pendiente') {
      return (
        <Badge variant="secondary" className="flex items-center gap-1 bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3" />
          Pendiente
        </Badge>
      );
    }
    if (log.leido) {
      return (
        <Badge variant="default" className="flex items-center gap-1 bg-green-100 text-green-800">
          <MailOpen className="w-3 h-3" />
          Leído
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="flex items-center gap-1 bg-blue-50 text-blue-700">
        <CheckCircle className="w-3 h-3" />
        Enviado
      </Badge>
    );
  };

  const getTipoBadge = (tipo) => {
    const tipoLabels = {
      'evento_del_dia': { label: 'Evento Hoy', color: 'bg-purple-100 text-purple-800' },
      'evento_proximo': { label: 'Evento Próximo', color: 'bg-indigo-100 text-indigo-800' },
      'nuevo_formulario_evaluacion': { label: 'Formulario', color: 'bg-cyan-100 text-cyan-800' },
      'manual': { label: 'Manual', color: 'bg-gray-100 text-gray-800' },
      'marketing_campana': { label: 'Marketing', color: 'bg-pink-100 text-pink-800' },
      'noticia_nueva': { label: 'Noticia', color: 'bg-orange-100 text-orange-800' },
      'beca_aprobada': { label: 'Beca Aprobada', color: 'bg-green-100 text-green-800' },
      'beca_rechazada': { label: 'Beca Rechazada', color: 'bg-red-100 text-red-800' }
    };
    
    const config = tipoLabels[tipo] || { label: tipo, color: 'bg-gray-100 text-gray-600' };
    return (
      <Badge variant="outline" className={`text-xs ${config.color}`}>
        {config.label}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Historial de Notificaciones</h1>
            <p className="text-gray-600">Registro de emails enviados por el sistema</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { loadLogs(); loadStats(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          {stats.total_fallidos > 0 && (
            <Button 
              onClick={handleResendAllFailed}
              disabled={resendingAll}
              className="bg-red-600 hover:bg-red-700"
            >
              {resendingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Reenviar Fallidos ({stats.total_fallidos})
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-xl font-bold">{total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Enviados</p>
                <p className="text-xl font-bold text-green-600">{stats.total_enviados || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <MailOpen className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Leídos</p>
                <p className="text-xl font-bold text-emerald-600">{stats.total_leidos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Sin Abrir</p>
                <p className="text-xl font-bold text-amber-600">{stats.total_no_leidos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Fallidos</p>
                <p className="text-xl font-bold text-red-600">{stats.total_fallidos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Tasa Apertura</p>
                <p className="text-xl font-bold text-purple-600">{stats.tasa_apertura || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar email o asunto..."
                value={filters.buscar}
                onChange={(e) => handleFilterChange('buscar', e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={filters.estado || 'all'} onValueChange={(v) => handleFilterChange('estado', v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="enviado">Enviado</SelectItem>
                <SelectItem value="fallido">Fallido</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.tipo || 'all'} onValueChange={(v) => handleFilterChange('tipo', v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {tiposDisponibles.map(tipo => (
                  <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.leido || 'all'} onValueChange={(v) => handleFilterChange('leido', v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Lectura" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Leídos</SelectItem>
                <SelectItem value="false">No leídos</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters}>
              Limpiar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="notifications-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-600">Fecha</th>
                  <th className="text-left p-4 font-medium text-gray-600">Tipo</th>
                  <th className="text-left p-4 font-medium text-gray-600">Asunto</th>
                  <th className="text-left p-4 font-medium text-gray-600">Destinatario</th>
                  <th className="text-left p-4 font-medium text-gray-600">Estado</th>
                  <th className="text-left p-4 font-medium text-gray-600">Leído</th>
                  <th className="text-center p-4 font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                      <p className="mt-2 text-gray-500">Cargando...</p>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">
                      <Mail className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No hay registros de notificaciones
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-600 whitespace-nowrap">
                        {formatDate(log.fecha_creacion)}
                      </td>
                      <td className="p-4">
                        {getTipoBadge(log.tipo_notificacion)}
                      </td>
                      <td className="p-4 max-w-xs truncate" title={log.asunto}>
                        {log.asunto}
                      </td>
                      <td className="p-4 text-gray-600">
                        {log.email_destinatario}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(log)}
                      </td>
                      <td className="p-4 text-center">
                        {log.leido ? (
                          <span className="text-green-600 text-xs">
                            {formatDate(log.fecha_leido)}
                          </span>
                        ) : log.estado === 'enviado' ? (
                          <span className="text-gray-400 text-xs">Sin abrir</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(log.id)}
                            title="Ver contenido"
                            data-testid={`preview-btn-${log.id}`}
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(log.id)}
                            disabled={resending === log.id}
                            title="Reenviar email"
                            data-testid={`resend-btn-${log.id}`}
                          >
                            {resending === log.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 text-orange-600" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-gray-600">
                Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Vista Previa del Email
            </DialogTitle>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            </div>
          ) : previewData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Destinatario</p>
                  <p className="font-medium">{previewData.email_destinatario}</p>
                </div>
                <div>
                  <p className="text-gray-500">Usuario</p>
                  <p className="font-medium">{previewData.usuario_nombre || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Fecha de Envío</p>
                  <p className="font-medium">{formatDate(previewData.fecha_creacion)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Estado</p>
                  {getStatusBadge(previewData)}
                </div>
                {previewData.leido && (
                  <>
                    <div>
                      <p className="text-gray-500">Fecha de Lectura</p>
                      <p className="font-medium">{formatDate(previewData.fecha_leido)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Veces Abierto</p>
                      <p className="font-medium">{previewData.veces_abierto || 1}</p>
                    </div>
                  </>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-gray-500 text-sm mb-2">Asunto</p>
                <p className="font-medium text-lg">{previewData.asunto}</p>
              </div>

              <div className="border-t pt-4">
                <p className="text-gray-500 text-sm mb-2">Contenido</p>
                <div 
                  className="bg-gray-50 rounded-lg p-4 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewData.mensaje }}
                />
              </div>

              <div className="border-t pt-4 flex gap-2">
                <Button 
                  onClick={() => handleResend(previewData.id)}
                  disabled={resending === previewData.id}
                >
                  {resending === previewData.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reenviar Email
                </Button>
                <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistorialNotificaciones;
